import "./products.css";
import "../services/service.css";
import SEO from "../../SEO";
import NeuralNetCanvas from "../../components/NeuralNetCanvas";

/* ─── SVG icon helper ────────────────────────────────────────────────────── */
const Icon = ({ d, d2, d3, circle, rect, poly, line, ...rest }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    {...rest}
  >
    {circle &&
      (Array.isArray(circle) ? (
        circle.map((c, i) => <circle key={i} {...c} />)
      ) : (
        <circle {...circle} />
      ))}
    {rect &&
      (Array.isArray(rect) ? (
        rect.map((r, i) => <rect key={i} {...r} />)
      ) : (
        <rect {...rect} />
      ))}
    {poly &&
      (Array.isArray(poly) ? (
        poly.map((p, i) => <polyline key={i} points={p} />)
      ) : (
        <polyline points={poly} />
      ))}
    {line &&
      (Array.isArray(line) ? (
        line.map((l, i) => <line key={i} {...l} />)
      ) : (
        <line {...line} />
      ))}
    {d && <path d={d} />}
    {d2 && <path d={d2} />}
    {d3 && <path d={d3} />}
  </svg>
);

/* ─── Bento feature cells ─────────────────────────────────────────────────── */
const bentoFeatures = [
  {
    size: "wide",
    accent: true,
    icon: {
      circle: [
        { cx: "5", cy: "12", r: "2" },
        { cx: "19", cy: "5", r: "2" },
        { cx: "19", cy: "19", r: "2" },
      ],
      line: [
        { x1: "7", y1: "11.5", x2: "17", y2: "6.5" },
        { x1: "7", y1: "12.5", x2: "17", y2: "17.5" },
      ],
    },
    title: "Connected Data Management",
    desc: "Organize related business information and structured operational data from centralized systems.",
    tags: [
      "Connected Data",
      "Relationship Mapping",
      "Centralized Management",
      "Linked Records",
    ],
  },
  {
    size: "med",
    icon: {
      d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z",
      d2: "M3.27 6.96L12 12.01l8.73-5.05",
      line: { x1: "12", y1: "22.08", x2: "12", y2: "12" },
    },
    title: "Relationship Visualization",
    desc: "Visualize connections between entities, workflows, operational activities, and related business information.",
  },
  {
    size: "third",
    icon: {
      d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
      d2: "M9 22V12h6v10",
    },
    title: "Centralized Knowledge Organization",
    desc: "Store and organize operational knowledge, records, workflows, and business-related information from one platform.",
  },
  {
    size: "third",
    icon: {
      circle: { cx: "11", cy: "11", r: "8" },
      line: { x1: "21", y1: "21", x2: "16.65", y2: "16.65" },
    },
    title: "Intelligent Search & Navigation",
    desc: "Access connected information faster through organized search and relationship-based navigation.",
  },
  {
    size: "third",
    icon: { poly: ["22 12 18 12 15 21 9 3 6 12 2 12"] },
    title: "Workflow & Entity Connectivity",
    desc: "Connect workflows, operational processes, and related entities through linked relationship structures.",
  },
];

/* ─── How it works steps ─────────────────────────────────────────────────── */
const steps = [
  {
    num: "01",
    title: "Ingest Your Data",
    desc: "Connect databases, APIs, documents, and data lakes. Knowledge Graph ingests raw data and normalises it into a unified graph schema automatically.",
  },
  {
    num: "02",
    title: "Extract Knowledge",
    desc: "AI models identify entities, classify relationships, and resolve duplicates — turning raw data into a rich, interlinked knowledge structure.",
  },
  {
    num: "03",
    title: "Query & Explore",
    desc: "Use the visual explorer or write graph queries to navigate relationships, find patterns, and surface insights hidden in flat data.",
  },
  {
    num: "04",
    title: "Power Your Apps",
    desc: "Expose graph data via API to fuel recommendation engines, fraud detectors, search systems, and AI models with structured contextual knowledge.",
  },
];

/* ─── Use cases ──────────────────────────────────────────────────────────── */
const useCases = [
  {
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    title: "Knowledge Management",
    desc: "Manage and organize operational knowledge, records, and business information from a centralized connected platform.",
    bullets: [
      "Knowledge management",
      "Internal knowledge systems",
      "Information organization",
    ],
  },
  {
    icon: {
      circle: [
        { cx: "5", cy: "12", r: "2" },
        { cx: "19", cy: "5", r: "2" },
        { cx: "19", cy: "19", r: "2" },
      ],
      line: [
        { x1: "7", y1: "11.5", x2: "17", y2: "6.5" },
        { x1: "7", y1: "12.5", x2: "17", y2: "17.5" },
      ],
    },
    title: "Connected Data Systems",
    desc: "Connect business data systems, entities, and operational records through structured relationship management.",
    bullets: [
      "Connected data systems",
      "Entity relationship mapping",
      "Business data connectivity",
    ],
  },
  {
    icon: {
      d: "M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z",
      d2: "M9 22V12h6v10",
    },
    title: "Workflow Relationship Tracking",
    desc: "Track relationships between workflows, operational processes, and business entities from a unified platform.",
    bullets: [
      "Workflow relationship tracking",
      "Operational visibility",
      "Process connectivity",
    ],
  },
  {
    icon: {
      circle: { cx: "11", cy: "11", r: "8" },
      line: { x1: "21", y1: "21", x2: "16.65", y2: "16.65" },
    },
    title: "Operational Visibility",
    desc: "Improve operational visibility and understand relationships between workflows, entities, and business processes.",
    bullets: [
      "Operational visibility",
      "Business data connectivity",
      "Relationship-based insights",
    ],
  },
];

/* ─── Capabilities ────────────────────────────────────────────────────────── */
const capabilities = [
  {
    icon: {
      d: "M12 2L2 7l10 5 10-5-10-5z",
      d2: "M2 17l10 5 10-5",
      d3: "M2 12l10 5 10-5",
    },
    label: "Better Data Organization",
    sub: "Organized connected records, structured relationship handling and centralized business knowledge",
  },
  {
    icon: {
      circle: { cx: "11", cy: "11", r: "8" },
      line: { x1: "21", y1: "21", x2: "16.65", y2: "16.65" },
    },
    label: "Faster Information Access",
    sub: "Improved search experience, faster data discovery and connected workflow visibility",
  },
  {
    icon: {
      circle: [
        { cx: "5", cy: "12", r: "2" },
        { cx: "19", cy: "5", r: "2" },
        { cx: "19", cy: "19", r: "2" },
      ],
      line: [
        { x1: "7", y1: "11.5", x2: "17", y2: "6.5" },
        { x1: "7", y1: "12.5", x2: "17", y2: "17.5" },
      ],
    },
    label: "Improved Operational Visibility",
    sub: "Linked operational workflows, connected business information and relationship-based insights",
  },
  {
    icon: {
      poly: ["22 11.08V12a10 10 0 1 1-5.93-9.14"],
      d: "M22 4L12 14.01l-3-3",
    },
    label: "Organized Operational Relationships",
    sub: "Track workflows, records, and operational entities through structured relationship mapping",
  },
];

/* ─── Stats ───────────────────────────────────────────────────────────────── */
const stats = [
  { value: "1B+", label: "Nodes Supported" },
  { value: "<50ms", label: "Query Response" },
  { value: "360°", label: "Data Visibility" },
  { value: "10×", label: "Search Accuracy" },
];

const chartBars = [45, 60, 40, 75, 55, 88, 70];

/* ════════════════════════════════════════════════════════════════════════════
   COMPONENT
   ════════════════════════════════════════════════════════════════════════════ */
export default function KnowledgeGraph() {
  return (
    <>
      <SEO
        title="Knowledge Graph: Data & Semantic Intelligence | Best Code Technology"
        description="Build intelligent knowledge graphs for better data relationships and insights."
        keywords="knowledge graph, AI data, semantic platform"
        url="https://bestcodetechnology.in/products/knowledge-graph"
      />
      <div className="uni-page">
        {/* ── HERO ── */}
        <section className="svc-hero svc-hero--neural">
          <NeuralNetCanvas />
          <div className="neural-bg-glow" aria-hidden="true" />
          <div className="container-xl">
            <div className="row justify-content-center">
              <div className="col-12 col-md-10 col-lg-8 text-center">
                <h1 className="svc-hero__headline">
                  Organize Connected Data &amp; <span>Relationships</span>
                </h1>
                <p className="svc-hero__desc">
                  Knowledge Graph is a structured data relationship platform
                  designed to connect, organize, and visualize linked business
                  information, entities, workflows, and operational knowledge
                  from centralized systems.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── BENTO FEATURES ── */}
        <section className="uni-bento">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Key Features</span>
                <h2 className="svc-section-title mt-3">
                  Core Features of Knowledge Graph
                </h2>
                <p className="svc-section-sub">
                  Knowledge Graph helps businesses manage connected information,
                  improve data visibility, and organize operational
                  relationships more efficiently.
                </p>
              </div>
            </div>

            <div className="uni-bento__grid">
              {bentoFeatures.map((f, i) => (
                <div
                  key={i}
                  className={`uni-bento__cell uni-bento__cell--${f.size}${f.accent ? " uni-bento__cell--accent" : ""}`}
                >
                  <div className="uni-bento__cell-icon">
                    <Icon {...f.icon} />
                  </div>
                  <h3 className="uni-bento__cell-title">{f.title}</h3>
                  <p className="uni-bento__cell-desc">{f.desc}</p>
                  {f.tags && (
                    <div className="uni-bento__tags">
                      {f.tags.map((t) => (
                        <span key={t} className="uni-bento__tag">
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="uni-steps">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">How It Works</span>
                <h2 className="svc-section-title mt-3">
                  From Raw Data to Connected Intelligence
                </h2>
                <p className="svc-section-sub">
                  No graph database expertise required. Knowledge Graph handles
                  the complexity — you focus on the insights.
                </p>
              </div>
            </div>

            <div className="row g-4 uni-steps__row">
              {steps.map((s) => (
                <div key={s.num} className="col-12 col-sm-6 col-lg-3">
                  <div className="uni-step">
                    <div className="uni-step__num">{s.num}</div>
                    <h4 className="uni-step__title">{s.title}</h4>
                    <p className="uni-step__desc">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── USE CASES ── */}
        <section className="uni-cases">
          <div className="container-xl">
            <div className="row justify-content-center mb-4 mb-lg-5">
              <div className="col-12 col-md-10 col-lg-7 text-center">
                <span className="pill-label">Use Cases</span>
                <h2 className="svc-section-title mt-3">
                  Knowledge Graph Use Cases
                </h2>
                <p className="svc-section-sub">
                  Knowledge Graph can be used across multiple operational and
                  information management environments.
                </p>
              </div>
            </div>

            <div className="row g-4">
              {useCases.map((uc, i) => (
                <div key={i} className="col-12 col-md-6 col-xl-3">
                  <div className="uni-case-card">
                    <div className="uni-case-card__icon">
                      <Icon {...uc.icon} />
                    </div>
                    <h4 className="uni-case-card__title">{uc.title}</h4>
                    <p className="uni-case-card__desc">{uc.desc}</p>
                    <ul className="uni-case-card__list">
                      {uc.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── CAPABILITIES ── */}
        <section className="uni-capabilities">
          <div className="container-xl">
            <div className="row align-items-center g-5">
              <div className="col-12 col-lg-5">
                <span className="pill-label">Advanced Capabilities</span>
                <h2 className="svc-section-title mt-3">
                  Why Businesses Use Knowledge Graph
                </h2>
                <p className="svc-section-sub svc-section-sub--left">
                  Knowledge Graph helps organizations improve operational
                  visibility and organize connected business information more
                  efficiently.
                </p>
              </div>

              <div className="col-12 col-lg-7">
                <div className="uni-cap-visual">
                  {capabilities.map((cap, i) => (
                    <div key={i} className="uni-cap-row">
                      <div className="uni-cap-row__icon">
                        <Icon {...cap.icon} />
                      </div>
                      <div>
                        <p className="uni-cap-row__label">{cap.label}</p>
                        <p className="uni-cap-row__sub">{cap.sub}</p>
                      </div>
                      <span className="uni-cap-row__badge">Included</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
